import httpGet from './httpClient/httpGet';
import envVar from '../config/envVars';
import httpPost from './httpClient/httpPost';
import httpPut from './httpClient/httpPut';
import { CreateSchoolOfficeUserRequest } from '../types/schoolCollection';
import { User } from '../types/userType';
import { UserRole } from '../enums';
import { isEmpty } from 'lodash';

export const getStudents = async (
  school: string,
  academicYear: string,
  grade: string,
  division: string
): Promise<User[]> => {
  const url = `${envVar.REACT_APP_BASE_API_URL}/users/school/${school}/academic-year/${academicYear}/grade/${grade}/division/${division}?role=currentStudent`;
  const response = await httpGet(url);
  return response as unknown as User[];
};

const getStudentsByVisibilityGroup = async (
  school: string,
  grade: string,
  division: string
): Promise<User[]> => {
  const baseUrl = `${envVar.REACT_APP_BASE_API_URL}/users?school=${school}&role=currentStudent`;
  const visibilityGroup = isEmpty(division)
    ? JSON.stringify({ type: 'GRADE', value: grade })
    : JSON.stringify({
        type: 'DIVISION',
        value: grade + ' ' + division
      });
  const response = await httpGet(
    `${baseUrl}&visibilityGroup=${encodeURIComponent(visibilityGroup)}`
  );
  return (response?.data || []) as unknown as User[];
};

const me = () => httpGet(`${envVar.REACT_APP_BASE_API_URL}/users/me`);

interface FetchUsersByRoleAndNameFilter {
  role: string;
  school?: string;
  staffName?: string;
  hasAdminAccess?: boolean;
}

const fetchUsersByRoleAndName = ({
  role,
  school,
  staffName,
  hasAdminAccess = false
}: FetchUsersByRoleAndNameFilter) => {
  let url = `${envVar.REACT_APP_BASE_API_URL}/users?role=${role}`;
  if (school) {
    url += `&school=${school}`;
  }
  if (staffName) {
    url += `&name=${staffName}`;
  }
  if (hasAdminAccess) {
    url += `&hasAdminAccess=true`;
  }
  return httpGet(url);
};
interface AddNewStaffUserProps {
  name: string;
  email: string;
  gender: string;
  role: string;
  school: string;
  memberCode?: string;
}

const addNewStaffUser = (formData: AddNewStaffUserProps) =>
  httpPost(`${envVar.REACT_APP_BASE_API_URL}/users`, formData);

const fetchUserById = (userId: string) =>
  httpGet(
    `${envVar.REACT_APP_BASE_API_URL}/users/${userId}?hasAdminAccess=true`
  );

const updateStaffDetail = (userId: string, formData: any) =>
  httpPut(`${envVar.REACT_APP_BASE_API_URL}/users/${userId}`, formData);

const uploadUserProfilePhoto = async (
  id: string,
  file: File,
  isGuardianProfile?: boolean
) => {
  const formData = new FormData();
  formData.append('file', file);

  const data = Object.fromEntries(formData.entries());
  let url = `${envVar.REACT_APP_BASE_API_URL}/users/${id}/profile/photo`;
  if (isGuardianProfile) {
    url += '?isGuardianProfile=true';
  }
  return httpPost(url, data, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
};

const updateUserStatus = (userId: string, status: string) =>
  httpPut(`${envVar.REACT_APP_BASE_API_URL}/users/${userId}/status`, {
    status: status
  });

const getUsers = async (school: string, includeAuth = true) =>
  httpGet(
    `${envVar.REACT_APP_BASE_API_URL}/users?school=${school}&role=schoolOffice`,
    {
      headers: !includeAuth
        ? {
            Authorization: 'Bearer '
          }
        : {}
    }
  );

const createUser = async (payload: CreateSchoolOfficeUserRequest) =>
  httpPost(`${envVar.REACT_APP_BASE_API_URL}/users`, {
    ...payload,
    role: UserRole.SCHOOL_OFFICE
  });

const updateUser = async (
  userId: string,
  payload: Partial<CreateSchoolOfficeUserRequest>
) => httpPut(`${envVar.REACT_APP_BASE_API_URL}/users/${userId}`, payload);

const updateCourseInfo = (userId: string, payload: any) =>
  httpPut(
    `${envVar.REACT_APP_BASE_API_URL}/users/school-staff/${userId}/course-info/classes`,
    payload
  );

const userApi = {
  getUsers,
  createUser,
  updateUser,
  updateUserStatus,
  me,
  fetchUsersByRoleAndName,
  addNewStaffUser,
  fetchUserById,
  updateStaffDetail,
  uploadUserProfilePhoto,
  updateCourseInfo,
  getStudentsByVisibilityGroup
};

export default userApi;
